<script setup lang="ts">
import { defineRule } from 'vee-validate';
import type { InputStylesEnum } from '~/types';
import { SelectStylesEnum } from '~/types';

const props = withDefaults(defineProps<{
  modelValue: string;
  rule?: string;
  styleType?: keyof typeof SelectStylesEnum;
  options: { label: string; value: string }[];
  selectOptions: {
    name: string;
    classes?: string;
  };
  labelOptions?: {
    text: string;
    classes?: string;
  };
  errorOptions?: {
    classes?: string;
  };
}>(), {
  styleType: SelectStylesEnum.purple,
});

const emits = defineEmits<{
  'update:modelValue': [string];
}>();

// Rules
defineRule('required', (value: string) => {
  if (!value || !value.length) {
    return 'This field is required';
  }

  return true;
});

//
const localValue = ref<string>('');
const isOpened = ref<boolean>(false);

const inputRef = ref<HTMLInputElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const dropdownItems = ref<HTMLElement[]>([]);
const dynamicItemHeight = ref<number>(0);

const isRequired = computed(() => props.rule?.includes('required'));
const selectedValue = computed(() => props.options.find(({ value }) => value === localValue.value)?.label ?? 'Select an option');

watch(() => props.modelValue, (text) => {
  localValue.value = text;
}, { immediate: true });

const open = () => isOpened.value = true;
const close = () => isOpened.value = false;

const selectOption = (value: string) => {
  localValue.value = value;
  emits('update:modelValue', value);
  close();
};

const clickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    close();
  }
};

onMounted(() => {
  document.addEventListener('click', clickOutside);
  nextTick(() => {
    if (dropdownItems.value.length) {
      dynamicItemHeight.value = dropdownItems.value[0].offsetHeight;
    }
  });
});

onBeforeUnmount(() => {
  document.removeEventListener('click', clickOutside);
});

// Styling
const stateStyles: Record<keyof typeof SelectStylesEnum, string> = {
  'purple': 'hover:border-viola-400 focus:border-viola-400',
  'light-peach': 'hover:border-fox-200 focus:border-fox-200',
};

const stateActiveStyles: Record<keyof typeof SelectStylesEnum, string> = {
  'purple': 'border-viola-400',
  'light-peach': 'border-fox-200',
};

const optionStyles: Record<keyof typeof SelectStylesEnum, string> = {
  'purple': 'hover:bg-viola-400 hover:text-white',
  'light-peach': 'hover:bg-fox-200 hover:text-white',
};

const labelStyles: Record<keyof typeof InputStylesEnum, string> = {
  'purple': 'text-viola-950',
  'light-peach': 'text-jungle-50',
};
</script>

<template>
  <div
    ref="dropdownRef"
    class="grid gap-2.5"
  >
    <label
      v-if="labelOptions?.text"
      :for="selectOptions.name"
      :class="[labelOptions.classes, labelStyles[styleType], 'font-medium leading-none text-md xs:text-lg 4k:text-xl']"
    >
      {{ labelOptions.text }}
      <span
        v-if="isRequired"
        class="text-fox-400"
      >*</span>
    </label>

    <div :class="[selectOptions.classes, stateStyles[styleType], isOpened && stateActiveStyles[styleType], 'border-2 border-transparent rounded-layout text-viola-950 bg-light-gray text-md py-2 px-3 outline-none focus:outline-none transition-all focus:[box-shadow:none] relative 4k:py-2.5 4k:px-4']">
      <VeeField
        v-bind="selectOptions"
        :id="selectOptions.name"
        ref="inputRef"
        v-model="localValue"
        type="text"
        class="opacity-0 absolute inset-0 cursor-pointer"
        :rules="rule"
        readonly
        :validate-on-blur="false"
        :validate-on-input="false"
        @focus="open"
      />
      <div>
        <div :class="['font-medium leading-[1.475] text-md xs:text-lg 4k:text-xl', { 'text-placeholder': !localValue }]">
          {{ selectedValue }}
        </div>

        <div class="absolute z-30 left-0 right-0 top-[calc(100%+5px)] bg-light-gray rounded-xl overflow-hidden">
          <div
            :class="['transition-[max-height] duration-300', { 'overflow-y-auto': isOpened }]"
            :style="{ maxHeight: isOpened ? `${dynamicItemHeight * 4}px` : '0' }"
          >
            <div
              v-for="({ label, value }) in options"
              :key="value"
              ref="dropdownItems"
              :class="[optionStyles[styleType], 'cursor-pointer py-2 px-3 transition-all text-sm xs:text-md 4k:py-2.5 4k:px-4 4k:text-lg']"
              @click="selectOption(value)"
            >
              {{ label }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <VeeErrorMessage
      :name="selectOptions.name"
      :class="[errorOptions?.classes, 'font-semibold text-md text-fox-400 4k:text-lg']"
    />
  </div>
</template>
